import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import {
  getWeeklyTimesheet, upsertTimesheetEntry, deleteTimesheetEntry, getTasks, getStaff,
  getTimesheetLocks, lockWeek, unlockWeek, updateTimesheetDescription,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileTimesheet from './mobile/MobileTimesheet';

interface Task { id: number; taskId: string; taskName: string; client?: { clientName: string }; category?: { name: string }; }
interface Staff { id: number; staffName: string; }
interface TimesheetEntry {
  id: number; staffId: number; taskId: number; date: string;
  hoursSpent: number; description?: string | null;
  task: { id: number; taskId: string; taskName: string; };
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DesktopTimesheet: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [allLocks, setAllLocks] = useState<{ weekStart: string; lockedBy: { staffName: string } }[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>(user?.staffId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rowTaskIds, setRowTaskIds] = useState<number[]>([]);
  const [newTaskId, setNewTaskId] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [taskDropOpen, setTaskDropOpen] = useState(false);
  const [warnings, setWarnings] = useState<Record<number, string>>({});
  const [lockMsg, setLockMsg] = useState('');
  const [descriptions, setDescriptions] = useState<Record<number, string>>({});
  const [savedDescriptions, setSavedDescriptions] = useState<Record<number, string>>({});
  // localHours holds unsaved edits: key = `${taskId}-${dayIndex}`, value = string typed by user
  const [localHours, setLocalHours] = useState<Record<string, string>>({});

  const weekStartStr = format(weekStart, 'yyyy-MM-dd');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tsRes, taskRes] = await Promise.all([
        getWeeklyTimesheet(selectedStaffId, weekStartStr),
        getTasks(),
      ]);
      const tsData = tsRes.data;
      const entriesArr: TimesheetEntry[] = Array.isArray(tsData) ? tsData : (tsData.entries || []);
      const locked: boolean = Array.isArray(tsData) ? false : (tsData.isLocked || false);
      setEntries(entriesArr);
      setIsLocked(locked);
      setTasks(taskRes.data);
      const seen = new Set<number>();
      const uniqueTaskIds: number[] = [];
      const descMap: Record<number, string> = {};
      entriesArr.forEach((e: TimesheetEntry) => {
        if (!seen.has(e.taskId)) { seen.add(e.taskId); uniqueTaskIds.push(e.taskId); }
        if (e.description && !descMap[e.taskId]) descMap[e.taskId] = e.description;
      });
      setRowTaskIds(uniqueTaskIds);
      setDescriptions(descMap);
      setSavedDescriptions(descMap);
      setLocalHours({});
    } finally {
      setLoading(false);
    }
  }, [selectedStaffId, weekStartStr]);

  useEffect(() => {
    getTasks().then((r) => setTasks(r.data));
    if (isAdmin) {
      getStaff().then((r) => setStaff(r.data));
      getTimesheetLocks().then((r) => setAllLocks(r.data || []));
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDayDate = (dayIndex: number) => addDays(weekStart, dayIndex);

  const getEntry = (taskId: number, dayIndex: number) => {
    const date = format(getDayDate(dayIndex), 'yyyy-MM-dd');
    return entries.find((e) => e.taskId === taskId && e.date.slice(0, 10) === date);
  };

  // Returns the effective hours for a cell — local edit takes precedence over server value
  const getCurrentHoursNum = useCallback((taskId: number, dayIndex: number): number => {
    const key = `${taskId}-${dayIndex}`;
    if (key in localHours) {
      const v = parseInt(localHours[key], 10);
      return isNaN(v) || v < 0 ? 0 : v;
    }
    const entry = getEntry(taskId, dayIndex);
    return entry ? Number(entry.hoursSpent) : 0;
  }, [localHours, entries, weekStart]); // eslint-disable-line

  const getDisplayHours = (taskId: number, dayIndex: number): string => {
    const key = `${taskId}-${dayIndex}`;
    if (key in localHours) return localHours[key];
    const entry = getEntry(taskId, dayIndex);
    return entry ? String(entry.hoursSpent) : '';
  };

  const isDirty = useMemo(() =>
    Object.keys(localHours).length > 0 ||
    rowTaskIds.some(tid => (descriptions[tid] || '') !== (savedDescriptions[tid] || '')),
    [localHours, descriptions, savedDescriptions, rowTaskIds]);

  const hasErrors = useMemo(() =>
    Object.values(warnings).some(w => !w.startsWith('⚠')),
    [warnings]);

  const validateHours = (value: string, taskId: number, dayIndex: number): string | null => {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0) return 'Invalid hours';
    if (!Number.isInteger(parsed)) return 'Only whole numbers allowed';
    if (parsed > 24) return 'Cannot exceed 24 hours/day';
    const dayTotal = rowTaskIds.reduce((sum, tid) => {
      if (tid === taskId) return sum + parsed;
      return sum + getCurrentHoursNum(tid, dayIndex);
    }, 0);
    if (dayTotal > 24) return `Day total (${dayTotal}h) exceeds 24h`;
    return null;
  };

  const handleHoursChange = (taskId: number, dayIndex: number, value: string) => {
    if (isLocked && !isAdmin) return;
    const key = `${taskId}-${dayIndex}`;
    const newWarnings = { ...warnings };
    if (value !== '' && !isNaN(parseInt(value, 10))) {
      const error = validateHours(value, taskId, dayIndex);
      if (error) {
        newWarnings[dayIndex * 1000 + taskId] = error;
      } else {
        const parsed = parseInt(value, 10);
        if (parsed > 12) newWarnings[dayIndex * 1000 + taskId] = `⚠ ${parsed}h seems high`;
        else delete newWarnings[dayIndex * 1000 + taskId];
      }
    } else {
      delete newWarnings[dayIndex * 1000 + taskId];
    }
    setWarnings(newWarnings);
    setLocalHours(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // Validate: every task with hours > 0 must have a description
    for (const taskId of rowTaskIds) {
      const hasHours = DAYS.some((_, i) => getCurrentHoursNum(taskId, i) > 0);
      if (hasHours && !(descriptions[taskId] || '').trim()) {
        alert(`Please add a description for "${getTaskLabel(taskId)}" before saving.`);
        return;
      }
    }
    setSaving(true);
    try {
      // Process changed hour cells
      for (const [key, value] of Object.entries(localHours)) {
        const parts = key.split('-');
        const taskId = parseInt(parts[0], 10);
        const dayIndex = parseInt(parts[1], 10);
        const date = format(getDayDate(dayIndex), 'yyyy-MM-dd');
        const parsed = parseInt(value, 10);
        const existing = getEntry(taskId, dayIndex);
        if (value === '' || isNaN(parsed) || parsed <= 0) {
          if (existing) await deleteTimesheetEntry(existing.id);
        } else {
          const desc = (descriptions[taskId] || '').trim();
          await upsertTimesheetEntry({ staffId: selectedStaffId, taskId, date, hoursSpent: parsed, description: desc });
        }
      }
      // Update descriptions that changed for tasks that already have server entries
      for (const taskId of rowTaskIds) {
        const currentDesc = (descriptions[taskId] || '').trim();
        const savedDesc = (savedDescriptions[taskId] || '').trim();
        if (currentDesc !== savedDesc) {
          const hasEntries = entries.some(e => e.taskId === taskId);
          if (hasEntries) {
            await updateTimesheetDescription({ staffId: selectedStaffId, taskId, weekStart: weekStartStr, description: currentDesc });
          }
        }
      }
      setLocalHours({});
      await fetchData();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error saving timesheet');
    } finally {
      setSaving(false);
    }
  };

  const confirmDiscard = () =>
    !isDirty || window.confirm('You have unsaved changes. Discard them?');

  const handleLockToggle = async () => {
    try {
      if (isLocked) {
        await unlockWeek(weekStartStr);
        setLockMsg(`✅ Week of ${weekStartStr} unlocked`);
      } else {
        await lockWeek(weekStartStr);
        setLockMsg(`🔒 Week of ${weekStartStr} locked — staff cannot backdate`);
      }
      await fetchData();
      getTimesheetLocks().then((r) => setAllLocks(r.data || []));
      setTimeout(() => setLockMsg(''), 4000);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error toggling lock');
    }
  };

  const addTaskRow = () => {
    const task = tasks.find((t) => t.taskId === newTaskId || String(t.id) === newTaskId);
    if (!task) return;
    if (!rowTaskIds.includes(task.id)) setRowTaskIds([...rowTaskIds, task.id]);
    setNewTaskId('');
    setTaskSearch('');
    setTaskDropOpen(false);
  };

  const removeRow = (taskId: number) => {
    setRowTaskIds(prev => prev.filter((id) => id !== taskId));
    setLocalHours(prev => {
      const next = { ...prev };
      DAYS.forEach((_, i) => { delete next[`${taskId}-${i}`]; });
      return next;
    });
  };

  const getDayTotal = (dayIndex: number) =>
    rowTaskIds.reduce((sum, taskId) => sum + getCurrentHoursNum(taskId, dayIndex), 0);

  const getRowTotal = (taskId: number) =>
    DAYS.reduce((sum, _, i) => sum + getCurrentHoursNum(taskId, i), 0);

  const weekTotal = DAYS.reduce((sum, _, i) => sum + getDayTotal(i), 0);

  const getTaskLabel = (taskId: number) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return `Task #${taskId}`;
    const parts: string[] = [`${task.taskId} — ${task.taskName}`];
    if (task.client?.clientName) parts.push(task.client.clientName);
    if (task.category?.name) parts.push(task.category.name);
    return parts.join(' · ');
  };

  const filteredTaskOptions = tasks
    .filter((t) => !rowTaskIds.includes(t.id))
    .filter((t) => {
      if (!taskSearch) return true;
      const q = taskSearch.toLowerCase();
      return (
        t.taskId.toLowerCase().includes(q) ||
        t.taskName.toLowerCase().includes(q) ||
        (t.client?.clientName || '').toLowerCase().includes(q) ||
        (t.category?.name || '').toLowerCase().includes(q)
      );
    });
  const staffName = isAdmin ? staff.find((s) => s.id === selectedStaffId)?.staffName : user?.staffName;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Weekly Timesheet</h2>
          {staffName && <p className="text-gray-500 text-sm mt-0.5">{staffName}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <select className="input-field w-52" value={selectedStaffId || ''}
              onChange={(e) => {
                if (!confirmDiscard()) return;
                setSelectedStaffId(Number(e.target.value));
              }}>
              <option value="">— Select Staff —</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
            </select>
          )}
          <button className="btn-secondary" onClick={() => { if (!confirmDiscard()) return; setWeekStart(subWeeks(weekStart, 1)); }}>‹ Prev</button>
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            {format(weekStart, 'dd MMM')} – {format(addDays(weekStart, 6), 'dd MMM yyyy')}
          </span>
          <button className="btn-secondary" onClick={() => { if (!confirmDiscard()) return; setWeekStart(addWeeks(weekStart, 1)); }}>Next ›</button>
          <button className="btn-secondary text-xs" onClick={() => { if (!confirmDiscard()) return; setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 })); }}>
            This Week
          </button>
          {(!isLocked || isAdmin) && (
            <button
              onClick={handleSave}
              disabled={!isDirty || saving || hasErrors}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                isDirty && !hasErrors
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:opacity-50'
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving…' : isDirty ? '💾 Save Changes' : '✓ Saved'}
            </button>
          )}
          {isAdmin && (
            <button onClick={handleLockToggle}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${
                isLocked ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200'
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}>
              {isLocked ? '🔒 Locked — Click to Unlock' : '🔓 Open — Click to Lock'}
            </button>
          )}
        </div>
      </div>

      {isLocked && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${isAdmin ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
          <span className="text-xl">🔒</span>
          <div>
            <p className={`text-sm font-semibold ${isAdmin ? 'text-orange-700' : 'text-red-700'}`}>
              This week is locked — backdating is not allowed
            </p>
            {isAdmin && <p className="text-xs text-orange-500">As Admin you can still edit and unlock.</p>}
          </div>
        </div>
      )}

      {lockMsg && <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700">{lockMsg}</div>}

      {isDirty && !saving && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-center gap-2">
          <span>●</span> You have unsaved changes — click <strong>Save Changes</strong> to save.
        </div>
      )}

      {Object.values(warnings).length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
          {Object.values(warnings).filter(Boolean).join(' · ')}
        </div>
      )}

      <div className="card overflow-x-auto p-0 hidden md:block">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="table-header w-64">Task</th>
              {DAYS.map((day, i) => (
                <th key={day} className="table-header text-center">
                  <div className="font-semibold">{day.slice(0, 3)}</div>
                  <div className="text-xs font-normal text-gray-400 mt-0.5">{format(getDayDate(i), 'dd/MM')}</div>
                </th>
              ))}
              <th className="table-header text-center">Total</th>
              <th className="table-header w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="table-cell text-center py-8 text-gray-400">Loading...</td></tr>
            ) : (
              <>
                {rowTaskIds.map((taskId) => (
                  <tr key={taskId} className="hover:bg-gray-50 border-b border-gray-100">
                    <td className="table-cell">
                      <div className="text-xs text-gray-700 font-medium leading-tight" title={getTaskLabel(taskId)}>{getTaskLabel(taskId)}</div>
                      <input
                        type="text"
                        className="mt-1 w-full text-xs px-2 py-1 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Description (required)"
                        value={descriptions[taskId] || ''}
                        onChange={(e) => setDescriptions((prev) => ({ ...prev, [taskId]: e.target.value }))}
                        disabled={isLocked && !isAdmin}
                      />
                    </td>
                    {DAYS.map((_, dayIndex) => {
                      const key = `${taskId}-${dayIndex}`;
                      const cellLocked = isLocked && !isAdmin;
                      const hasWarning = !!warnings[dayIndex * 1000 + taskId];
                      const isEdited = key in localHours;
                      return (
                        <td key={dayIndex} className="table-cell text-center p-1">
                          <input
                            type="number" min="0" max="24" step="1"
                            value={getDisplayHours(taskId, dayIndex)}
                            disabled={cellLocked}
                            onChange={(e) => handleHoursChange(taskId, dayIndex, e.target.value)}
                            className={`w-16 text-center border rounded px-1 py-1 text-sm focus:outline-none focus:ring-1 ${
                              cellLocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' :
                              hasWarning ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-500' :
                              isEdited ? 'border-blue-300 bg-blue-50 focus:ring-blue-500' :
                              'border-gray-200 focus:ring-blue-500'
                            }`}
                            placeholder="0"
                          />
                        </td>
                      );
                    })}
                    <td className="table-cell text-center font-semibold text-blue-700">{getRowTotal(taskId) || '—'}</td>
                    <td className="table-cell">
                      {(!isLocked || isAdmin) && (
                        <button onClick={() => removeRow(taskId)} className="text-gray-400 hover:text-red-500 text-xs">✕</button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                  <td className="table-cell text-gray-700">Daily Total</td>
                  {DAYS.map((_, i) => (
                    <td key={i} className={`table-cell text-center ${getDayTotal(i) > 12 ? 'text-orange-500' : 'text-blue-700'}`}>
                      {getDayTotal(i) || '—'}{getDayTotal(i) > 12 && <span className="text-xs ml-0.5">⚠</span>}
                    </td>
                  ))}
                  <td className="table-cell text-center text-blue-700 text-base">{weekTotal}</td>
                  <td className="table-cell"></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {(!isLocked || isAdmin) && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <input
              type="text"
              className="input-field w-72"
              placeholder="Search task by name, ID, client, category..."
              value={taskSearch}
              onFocus={() => setTaskDropOpen(true)}
              onBlur={() => setTimeout(() => setTaskDropOpen(false), 150)}
              onChange={(e) => { setTaskSearch(e.target.value); setNewTaskId(''); setTaskDropOpen(true); }}
            />
            {taskDropOpen && filteredTaskOptions.length > 0 && (
              <div className="absolute z-20 top-full left-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto w-80">
                {filteredTaskOptions.slice(0, 50).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setNewTaskId(String(t.id));
                      setTaskSearch(`${t.taskId} — ${t.taskName}`);
                      setTaskDropOpen(false);
                    }}
                  >
                    <div className="text-sm font-medium text-gray-800">{t.taskId} — {t.taskName}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {t.client?.clientName || 'No client'}{t.category?.name ? ` · ${t.category.name}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="btn-primary" onClick={addTaskRow} disabled={!newTaskId}>Add Row</button>
          {weekTotal > 0 && (
            <span className="text-sm text-gray-600 ml-auto">Week Total: <span className="font-bold text-blue-700">{weekTotal} hrs</span></span>
          )}
        </div>
      )}

      {isAdmin && allLocks.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">🔒 All Locked Weeks</h3>
          <div className="flex flex-wrap gap-2">
            {allLocks.map((lock) => (
              <div key={lock.weekStart} className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 text-xs">
                <span className="font-medium text-red-700">{lock.weekStart?.slice(0, 10)}</span>
                <span className="text-red-400">by {lock.lockedBy?.staffName}</span>
                <button onClick={async () => { await unlockWeek(lock.weekStart?.slice(0, 10)); getTimesheetLocks().then((r) => setAllLocks(r.data || [])); }}
                  className="text-red-400 hover:text-red-700 ml-1 font-bold">✕</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Timesheet: React.FC = () => (useIsMobile() ? <MobileTimesheet /> : <DesktopTimesheet />);

export default Timesheet;
